import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { getContentId } from '../types';

interface PageToggleRow {
  field: string;
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface PageTogglesProps {
  siteId: string;
  pages: PageToggleRow[];
}

/**
 * Master toggle list for enabling/disabling all optional site pages.
 * Renders a list of toggle switches with links to each editor page.
 */
export default function PageToggles({ siteId, pages }: PageTogglesProps) {
  const { items, loading, update } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const [toggling, setToggling] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, boolean>>({});

  const siteItem = items[0];

  useEffect(() => {
    if (siteItem) {
      const data = siteItem.data as Record<string, unknown>;
      const v: Record<string, boolean> = {};
      for (const page of pages) {
        v[page.field] = data[page.field] === true || data[page.field] === 'true';
      }
      setValues(v);
    }
  }, [siteItem, pages]);

  const handleToggle = useCallback(async (field: string, label: string) => {
    if (!siteItem || toggling) return;
    const newValue = !values[field];
    setValues((prev) => ({ ...prev, [field]: newValue }));
    setToggling(field);
    try {
      const id = getContentId(siteItem.contentKey);
      const updatedData = { ...(siteItem.data as Record<string, unknown>), [field]: newValue };
      await update(id, updatedData);
      addToast('success', newValue ? `${label} enabled` : `${label} disabled`);
    } catch (err) {
      setValues((prev) => ({ ...prev, [field]: !newValue }));
      addToast('error', err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setToggling(null);
    }
  }, [siteItem, toggling, values, update, addToast]);

  if (loading || !siteItem) return null;

  return (
    <div className="page-toggles">
      {pages.map((page) => (
        <div key={page.field} className="page-toggle-row">
          <span className="page-toggle-icon">{page.icon}</span>
          <Link to={page.path} className="page-toggle-label">{page.label}</Link>
          <button
            className={`section-toggle-switch ${values[page.field] ? 'active' : ''}`}
            onClick={() => handleToggle(page.field, page.label)}
            disabled={toggling === page.field}
            title={values[page.field] ? `Hide ${page.label}` : `Show ${page.label}`}
            role="switch"
            aria-checked={values[page.field] || false}
          >
            <span className="section-toggle-knob" />
          </button>
        </div>
      ))}
    </div>
  );
}
